import { useState, useCallback } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { t } from "@/i18n-js/instance";
import { BlockDmsModal } from "@/react/components/Chat/sharedV2/BlockDmsModal";
import { useProfileDrawer } from "@/react/components/Drawers/ProfileDrawer";
import { useBlockDms } from "@/react/hooks/chatsV2/blockDms/useBlockDms";
import type { ChatRoomParticipant } from "@/react/types/Chat/ChatRoomParticipant";
import { Typography } from "@circle-react/components/shared/uikit/Typography";
import {
  useChatParticipantsApi,
  useChatRoom,
} from "@circle-react/hooks/chatsV2";
import { Loader } from "@circle-react-shared/uikit/Loader";
import { ParticipantList } from "./ParticipantList";
import { mapParticipantsPerStatus, sortParticipants } from "./helpers";
import { useParticipantActions } from "./useParticipantActions";
import { useBlockMember } from "@/react/components/Spaces/ChatSpace/RailBar/hooks/useBlockMember";
import { useCanMessageMember } from "@/react/components/Profile/useCanMessageMember";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { DIRECT_CHAT_ROOM } from "@/react/components/constants";
import {
  chatRoomPath,
  getRouteWithPrevLocation,
} from "@/react/helpers/urlHelpers";
import { useHistory } from "react-router-dom";

export interface MemberListProps {
  chatRoom: any;
  refetchChatRoom: () => void;
}

export const MemberList = ({ chatRoom, refetchChatRoom }: MemberListProps) => {
  const memberProfileDrawer = useProfileDrawer();
  const [isBlockDmsModalOpen, setIsBlockDmsModalOpen] = useState(false);
  const { uuid, current_participant: currentParticipant } = chatRoom;
  const { canBlockMemberDm } = useBlockDms();
  const { shouldShowMessageOption } = useCanMessageMember();
  const { isViewOnlyMasquerading } = usePunditUserContext();
  const { findOrCreateChatRoom } = useChatRoom();
  const history = useHistory();

  const {
    chatParticipants = [],
    refetchChatParticipants,
    totalCount: totalParticipantsCount,
    hasNextPage,
    fetchNextPage,
  } = useChatParticipantsApi(uuid);

  const participants = [...sortParticipants(chatParticipants)];
  const { handleMakeAdmin, removeParticipant } = useParticipantActions({
    refetchChatParticipants,
    refetchChatRoom,
    uuid,
  });

  const showMemberProfile = useCallback(
    (memberPublicId: string) => {
      if (memberPublicId) {
        void memberProfileDrawer.show({
          memberPublicId,
        });
      }
    },
    [memberProfileDrawer],
  );

  const onSuccessBlockMember = () => {
    refetchChatParticipants();
  };

  const {
    blockMember,
    isBlocking,
    memberToBlock,
    setMemberToBlock,
    unBlockMember,
    isUnblocking,
  } = useBlockMember({
    setIsBlockDmsModalOpen,
    onSuccess: onSuccessBlockMember,
  });

  const toggleBlockDmsModal = useCallback(
    (participant: ChatRoomParticipant) => {
      setMemberToBlock(participant);
      setIsBlockDmsModalOpen(true);
    },
    [setMemberToBlock, setIsBlockDmsModalOpen],
  );

  const handleBlockMember = useCallback(() => {
    if (memberToBlock) {
      blockMember({ memberId: memberToBlock.community_member_id });
    }
  }, [memberToBlock, blockMember]);

  const handleUnblockMember = useCallback(
    (member: any) => {
      if (!isUnblocking) {
        unBlockMember({
          memberId: member?.community_member_id,
          name: member?.name,
        });
      }
    },
    [isUnblocking, unBlockMember],
  );

  const actionClassName = "!py-2.5 !px-3";
  const errorClasses = {
    iconClassName: "text-feedback-error",
    color: "text-feedback-error",
  };

  const handleDirectMessaging = async (participant: ChatRoomParticipant) => {
    const chatRoomUuid = await findOrCreateChatRoom({
      kind: DIRECT_CHAT_ROOM,
      community_member_ids: [participant?.community_member_id],
    });
    if (chatRoomUuid) {
      history.push(
        getRouteWithPrevLocation({
          url: chatRoomPath({ uuid: chatRoomUuid }),
        }),
      );
    }
  };

  const getProfileActionItems = (participant: ChatRoomParticipant) => {
    const { is_blocked: isBlocked } = participant;
    const profileActionItems: any[] = [
      {
        value: "view-profile",
        icon: "20-eye-open",
        iconSize: 20,
        className: actionClassName,
        label: t("view_profile"),
        onClick: () => showMemberProfile(participant.user_public_uid || ""),
      },
      {
        value: participant.admin ? "remove-admin" : "make-admin",
        label: participant.admin
          ? t("messaging.remove_admin")
          : t("messaging.make_admin"),
        icon: "20-moderator",
        iconSize: 20,
        className: actionClassName,
        onClick: () =>
          handleMakeAdmin({
            participantId: participant.id,
            status: !participant.admin,
          }),
      },
    ];

    if (shouldShowMessageOption(participant)) {
      profileActionItems.push({
        value: "message",
        icon: "20-dm-message",
        className: "!py-2.5 !px-3",
        iconSize: 20,
        label: t("messaging.send_direct_message"),
        onClick: () =>
          !isViewOnlyMasquerading && handleDirectMessaging(participant),
      });
    }

    if (
      canBlockMemberDm({
        admin: participant.community_admin,
        community_member_id: participant.community_member_id,
      })
    ) {
      if (isBlocked) {
        profileActionItems.push({
          value: "block-dm",
          icon: "20-circle-check",
          className: actionClassName,
          iconSize: 20,
          label: t("messaging.unblock_direct_messages"),
          onClick: () => handleUnblockMember(participant),
        });
      } else {
        profileActionItems.push({
          value: "block-dm",
          icon: "20-block-dms",
          className: actionClassName,
          iconSize: 20,
          label: t("messaging.block_direct_messages"),
          onClick: () => toggleBlockDmsModal(participant),
          ...errorClasses,
        });
      }
    }

    profileActionItems.push({
      value: "remove",
      label: t("messaging.remove"),
      icon: "20-close",
      iconSize: 20,
      className: actionClassName,
      onClick: () => removeParticipant({ participantId: participant.id }),
      ...errorClasses,
    });
    return profileActionItems;
  };

  const { onlineParticipants, offlineParticipants } =
    mapParticipantsPerStatus(participants);

  return (
    <>
      <div
        id="scrollableDiv"
        className="messaging-rail__content h-full overflow-auto"
      >
        <InfiniteScroll
          scrollThreshold={0.8}
          next={fetchNextPage}
          hasMore={Boolean(hasNextPage)}
          dataLength={totalParticipantsCount}
          className="px-6 pb-6"
          scrollableTarget="scrollableDiv"
          loader={<Loader center />}
        >
          {onlineParticipants.length > 0 && (
            <>
              <div className="text-default mb-2">
                <Typography.LabelXxs weight="bold" color="text-default">
                  {t("chat_space.online")}
                </Typography.LabelXxs>
              </div>
              <ParticipantList
                currentParticipant={currentParticipant}
                getProfileActionItems={getProfileActionItems}
                participantList={onlineParticipants}
                showMemberProfile={showMemberProfile}
              />
            </>
          )}
          {offlineParticipants.length > 0 && (
            <>
              <div className="text-default my-2">
                <Typography.LabelXxs weight="bold" color="text-default">
                  {t("chat_space.offline")}
                </Typography.LabelXxs>
              </div>
              <ParticipantList
                currentParticipant={currentParticipant}
                getProfileActionItems={getProfileActionItems}
                participantList={offlineParticipants}
                showMemberProfile={showMemberProfile}
              />
            </>
          )}
        </InfiniteScroll>
      </div>
      <BlockDmsModal
        showModal={isBlockDmsModalOpen}
        onClose={() => setIsBlockDmsModalOpen(false)}
        onConfirm={handleBlockMember}
        memberName={memberToBlock?.name || ""}
        isLoading={isBlocking}
      />
    </>
  );
};
